import React, { useState } from "react";
import { Sparkles, Plus, Pencil } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../../components/ui/select";
import CatalogTable from "../components/CatalogTable";
import { CatalogLoadingCard, CatalogErrorCard } from "../components/CatalogStateCard";
import EntityFormDialog from "../components/EntityFormDialog";
import StatusBadge from "../components/StatusBadge";
import { useEmailCatalogEntityList } from "../../../hooks/useEmailCatalogEntityList";
import { getEmailMarketingAngles, createAngle, updateAngle } from "../../../lib/emailMarketingApi";

// EMAILS-IMPL-007: Angle authoring. Angle is a standalone governed entity
// (ANG-SPEC-001 -- no Campaign/Program parent field exists on the runtime
// model or is implied here). Status uses the real, governed 6-state
// vocabulary (email_marketing_catalog.py's AngleStatus) -- never an
// invented set of options.
export const ANGLE_STATUS_VALUES = ["Draft", "Review", "Approved", "Active", "Retired", "Archived"];

function emptyForm() {
  return { name: "", status: "Draft" };
}

export default function AnglesPanel() {
  const { items, loading, error, mutating, mutationError, clearMutationError, createItem, updateItem } =
    useEmailCatalogEntityList({
      list: getEmailMarketingAngles,
      create: createAngle,
      update: updateAngle,
      getId: (row) => row.angle_id,
    });

  const [expandedId, setExpandedId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm());
  const [editingAngle, setEditingAngle] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm());

  const openCreate = () => {
    setCreateForm(emptyForm());
    clearMutationError();
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    try {
      await createItem({ name: createForm.name.trim(), status: createForm.status });
      setCreateOpen(false);
    } catch {
      // handled via mutationError
    }
  };

  const openEdit = (angle) => {
    setEditingAngle(angle);
    setEditForm({ name: angle.name, status: angle.status });
    clearMutationError();
  };

  const submitEdit = async () => {
    try {
      await updateItem(editingAngle.angle_id, {
        name: editForm.name.trim() !== editingAngle.name ? editForm.name.trim() : undefined,
        status: editForm.status !== editingAngle.status ? editForm.status : undefined,
      });
      setEditingAngle(null);
    } catch {
      // handled via mutationError
    }
  };

  if (loading) return <CatalogLoadingCard testId="angles-loading" />;
  if (error) return <CatalogErrorCard message={error} testId="angles-error" />;

  return (
    <div className="space-y-4" data-testid="angles-panel">
      <CatalogTable
        testId="angles-table"
        title="Angles"
        icon={Sparkles}
        headerAction={
          <Button
            type="button"
            size="sm"
            className="gap-1.5 bg-violet-600 text-white hover:bg-violet-500"
            onClick={openCreate}
            data-testid="new-angle-button"
          >
            <Plus className="h-4 w-4" /> New Angle
          </Button>
        }
        rows={items}
        getRowKey={(row) => row.angle_id}
        onRowClick={(row) => setExpandedId(expandedId === row.angle_id ? null : row.angle_id)}
        emptyTitle="No Angles yet"
        emptySubtitle='Create one with "New Angle" to get started.'
        columns={[
          {
            key: "name",
            label: "Angle",
            render: (row) => (
              <div>
                <div className="text-sm font-medium text-slate-100">{row.name}</div>
                <div className="text-xs text-slate-400">{row.angle_id}</div>
              </div>
            ),
          },
          { key: "status", label: "Status", render: (row) => <StatusBadge status={row.status} testId={`angle-status-${row.angle_id}`} /> },
          { key: "version", label: "Version", render: (row) => <span className="font-mono text-sm text-slate-300">{row.version || "—"}</span> },
        ]}
      />

      {items
        .filter((row) => row.angle_id === expandedId)
        .map((row) => (
          <div key={row.angle_id} className="rounded-lg border border-white/10 bg-white/5 p-4" data-testid={`angle-detail-${row.angle_id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
                <Detail label="Angle ID" value={row.angle_id} mono />
                <Detail label="Status" value={row.status} />
                <Detail label="Version" value={row.version || "—"} mono />
                <Detail label="Created" value={row.created_at ? new Date(row.created_at).toLocaleString() : "—"} />
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="gap-1.5 border-white/10 text-slate-200"
                onClick={() => openEdit(row)}
                data-testid={`edit-angle-${row.angle_id}`}
              >
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Button>
            </div>
          </div>
        ))}

      <EntityFormDialog
        testId="create-angle-dialog"
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="New Angle"
        description="Angles define the psychological strategy — how customer beliefs should change."
        onSubmit={submitCreate}
        submitting={mutating}
        error={mutationError}
        submitLabel="Create Angle"
      >
        <div className="space-y-2">
          <Label htmlFor="angle-name">Name</Label>
          <Input
            id="angle-name"
            value={createForm.name}
            onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Loss Aversion"
            required
            data-testid="angle-name-input"
          />
        </div>
        <StatusField value={createForm.status} onChange={(status) => setCreateForm((f) => ({ ...f, status }))} testId="angle-status-select" />
      </EntityFormDialog>

      {editingAngle && (
        <EntityFormDialog
          testId="edit-angle-dialog"
          open={!!editingAngle}
          onOpenChange={(open) => !open && setEditingAngle(null)}
          title={`Edit ${editingAngle.angle_id}`}
          onSubmit={submitEdit}
          submitting={mutating}
          error={mutationError}
          submitLabel="Save Changes"
        >
          <div className="space-y-2">
            <Label htmlFor="angle-edit-name">Name</Label>
            <Input
              id="angle-edit-name"
              value={editForm.name}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              required
              data-testid="angle-edit-name-input"
            />
          </div>
          <StatusField value={editForm.status} onChange={(status) => setEditForm((f) => ({ ...f, status }))} testId="angle-edit-status-select" />
        </EntityFormDialog>
      )}
    </div>
  );
}

function StatusField({ value, onChange, testId }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={testId}>Status</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={testId} data-testid={testId}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {ANGLE_STATUS_VALUES.map((s) => (
            <SelectItem key={s} value={s} data-testid={`${testId}-option-${s}`}>
              {s}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={mono ? "font-mono text-slate-200" : "text-slate-200"}>{value}</div>
    </div>
  );
}
